/**
 * Fleet Summary Cards Component
 * 
 * Displays summary statistics for the generated fleet in card format
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, Alert, Tooltip } from '@mui/material';

import { FleetSummaryCardsProps } from './types';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
const FleetSummaryCards: React.FC<FleetSummaryCardsProps> = ({ summary, mixedFleetSummary, mode }) => {
  const { t } = useTranslation('mapWizards');
  const isMixedMode = mode === 'mixed' && mixedFleetSummary;

  const baseCards = [
    {
      key: 'fleetSize',
      title: t('autoFleetGen.summary.fleetSizeTitle'),
      value: t(
        summary.totalVehicles === 1
          ? 'autoFleetGen.summary.fleetSizeValueOne'
          : 'autoFleetGen.summary.fleetSizeValueOther',
        { total: summary.totalVehicles }
      ),
      subtitle: isMixedMode
        ? t('autoFleetGen.summary.fleetSizeMixedSubtitle', {
            fixed: mixedFleetSummary.vehiclesFixedOnly,
            onDemand: mixedFleetSummary.vehiclesOnDemandOnly,
            mixed: mixedFleetSummary.vehiclesMixed,
          })
        : t(
            summary.totalShifts === 1
              ? 'autoFleetGen.summary.fleetSizeShiftsOne'
              : 'autoFleetGen.summary.fleetSizeShiftsOther',
            { total: summary.totalShifts }
          ),
      icon: <Icon size={18} name="bus" />,
      color: '#1976d2',
      bgColor: '#e3f2fd',
    },
    {
      key: 'servicePeriod',
      title: t('autoFleetGen.summary.servicePeriodTitle'),
      value: summary.servicePeriod,
      subtitle: t('autoFleetGen.summary.servicePeriodSubtitle'),
      icon: <Icon size={18} name="clock" />,
      color: '#00897b',
      bgColor: '#e0f2f1',
    },
    {
      key: 'totalCapacity',
      title: t('autoFleetGen.summary.totalCapacityTitle'),
      value: t('autoFleetGen.summary.totalCapacityValue', { total: summary.totalCapacity }),
      subtitle: t('autoFleetGen.summary.totalCapacitySubtitle', { standard: summary.totalStandardSeats, wheelchair: summary.totalWheelchairSeats }),
      icon: <Icon size={18} name="users" />,
      color: '#388e3c',
      bgColor: '#e8f5e9',
    },
    {
      key: 'vehicleHours',
      title: t('autoFleetGen.summary.vehicleHoursTitle'),
      value: t('autoFleetGen.summary.vehicleHoursValue', { value: summary.activeVehicleHours }),
      subtitle: t('autoFleetGen.summary.vehicleHoursSubtitle'),
      icon: <Icon size={18} name="clock" />,
      color: '#f57c00',
      bgColor: '#fff3e0',
    },
    {
      key: 'breakPeriods',
      title: t('autoFleetGen.summary.breakPeriodsTitle'),
      value: summary.totalBreaks,
      subtitle: t('autoFleetGen.summary.breakPeriodsSubtitle'),
      icon: <Icon size={18} name="coffee" />,
      color: '#7b1fa2',
      bgColor: '#f3e5f5',
    },
  ];

  // Add mixed fleet specific cards
  const mixedCards = isMixedMode ? [
    {
      key: 'serviceBreakdown',
      title: t('autoFleetGen.summary.serviceBreakdownTitle'),
      value: `${mixedFleetSummary.serviceTypeBreakdown.fixedRoutePercent.toFixed(1)}% / ${mixedFleetSummary.serviceTypeBreakdown.onDemandPercent.toFixed(1)}%`,
      subtitle: t('autoFleetGen.summary.serviceBreakdownSubtitle'),
      icon: <Icon size={18} d={ICON_FALLBACKS.SwapHoriz} />,
      color: '#ff9800',
      bgColor: '#fff3e0',
    },
    {
      key: 'coverageStatus',
      title: t('autoFleetGen.summary.coverageTitle'),
      value: mixedFleetSummary.allFixedRouteRequirementsMet && mixedFleetSummary.onDemandCoveragePercent >= 100
        ? `✓ ${t('autoFleetGen.summary.coverageComplete')}`
        : mixedFleetSummary.allFixedRouteRequirementsMet
        ? t('autoFleetGen.summary.coveragePercent', { value: mixedFleetSummary.onDemandCoveragePercent.toFixed(0) })
        : `⚠ ${t('autoFleetGen.summary.coveragePartial')}`,
      subtitle: mixedFleetSummary.allFixedRouteRequirementsMet && mixedFleetSummary.onDemandCoveragePercent >= 100
        ? t('autoFleetGen.summary.coverageAllMet')
        : mixedFleetSummary.allFixedRouteRequirementsMet
        ? t('autoFleetGen.summary.coverageOnDemand')
        : t('autoFleetGen.summary.coverageFixedIncomplete'),
      icon: mixedFleetSummary.allFixedRouteRequirementsMet && mixedFleetSummary.onDemandCoveragePercent >= 100
        ? <Icon size={18} name="circle-check" />
        : <Icon size={18} name="triangle-exclamation" />,
      color: mixedFleetSummary.allFixedRouteRequirementsMet && mixedFleetSummary.onDemandCoveragePercent >= 100
        ? '#388e3c'
        : '#f57c00',
      bgColor: mixedFleetSummary.allFixedRouteRequirementsMet && mixedFleetSummary.onDemandCoveragePercent >= 100
        ? '#e8f5e9'
        : '#fff3e0',
    },
    {
      key: 'transitions',
      title: t('autoFleetGen.summary.transitionsTitle'),
      value: mixedFleetSummary.transitionCount,
      subtitle: t('autoFleetGen.summary.transitionsSubtitle'),
      icon: <Icon size={18} d={ICON_FALLBACKS.TrendingUp} />,
      color: '#9c27b0',
      bgColor: '#f3e5f5',
    },
  ] : [];

  const summaryCards = [...baseCards, ...mixedCards];
  const isFiveCardStandardLayout = summaryCards.length === 5;

  return (
    <Box sx={{ mb: 3, width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        {t('autoFleetGen.summary.title')}
      </Typography>
      
      <Box
        sx={{
          display: 'grid',
          gap: 1,
          width: '100%',
          gridTemplateColumns: isFiveCardStandardLayout
            ? 'repeat(5, minmax(0, 1fr))'
            : 'repeat(6, minmax(0, 1fr))',
        }}
      >
          {summaryCards.map((card, index) => (
            <Paper
              key={index}
              sx={{
                p: isFiveCardStandardLayout ? 1 : 1.25,
                gridColumn: isFiveCardStandardLayout
                  ? 'span 1'
                  : index < 3
                    ? 'span 2'
                    : summaryCards.length > 5
                      ? 'span 2'
                      : 'span 3',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: card.bgColor,
                border: `1px solid ${card.color}20`,
                transition: 'all 0.2s ease',
                overflow: 'hidden',
                minWidth: 0,
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 0.4, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: `${card.color}1A`,
                    color: card.color,
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.1, fontSize: '0.74rem' }}>
                    {card.title}
                    {card.key === 'vehicleHours' ? (
                      <Tooltip
                        title={t('autoFleetGen.summary.vehicleHoursTooltip')}
                        arrow
                      >
                        <Box component="span" sx={{ display: 'inline-flex', ml: 0.5, verticalAlign: 'middle', cursor: 'help' }}>
                          <Icon size={14} name="circle-info" />
                        </Box>
                      </Tooltip>
                    ) : null}
                </Typography>
              </Box>
              <Typography
                variant={card.key === 'vehicleHours' || card.key === 'serviceBreakdown' ? 'h6' : 'h5'}
                sx={{
                  fontWeight: 700,
                  color: card.color,
                  lineHeight: 1.15,
                  whiteSpace: card.key === 'servicePeriod' ? 'nowrap' : 'normal',
                  overflow: card.key === 'servicePeriod' ? 'hidden' : 'visible',
                  textOverflow: card.key === 'servicePeriod' ? 'ellipsis' : 'clip',
                  fontSize:
                    card.key === 'servicePeriod'
                      ? '1.02rem'
                      : card.key === 'vehicleHours' || card.key === 'serviceBreakdown'
                        ? '1.08rem'
                        : isFiveCardStandardLayout
                          ? '1.34rem'
                          : undefined,
                }}
              >
                {card.value}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: '0.78rem',
                  lineHeight: 1.2,
                  display: 'block',
                  mt: 0.15,
                  whiteSpace: card.key === 'servicePeriod' ? 'nowrap' : 'normal',
                  overflow: card.key === 'servicePeriod' ? 'hidden' : 'visible',
                  textOverflow: card.key === 'servicePeriod' ? 'ellipsis' : 'clip',
                }}
              >
                {card.key === 'totalCapacity' ? (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8 }}>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                      <Icon size={13} d={ICON_FALLBACKS.EventSeatOutlined} />
                      {summary.totalStandardSeats}
                    </Box>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                      <Icon size={13} name="wheelchair-move" />
                      {summary.totalWheelchairSeats}
                    </Box>
                  </Box>
                ) : card.key === 'vehicleHours' ? (
                  null
                ) : (
                  card.subtitle
                )}
              </Typography>
            </Paper>
          ))}
      </Box>

      {/* Mixed Fleet Warnings */}
      {isMixedMode && mixedFleetSummary.warnings && (
        <Box sx={{ mt: 2 }}>
          {mixedFleetSummary.warnings.reducedFrequencyRoutes && mixedFleetSummary.warnings.reducedFrequencyRoutes.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {t('autoFleetGen.summary.reducedFrequencyTitle')}
              </Typography>
              <Typography variant="caption">
                {mixedFleetSummary.warnings.reducedFrequencyRoutes.join(', ')}
              </Typography>
            </Alert>
          )}
          {mixedFleetSummary.warnings.limitedCoveragePeriods && mixedFleetSummary.warnings.limitedCoveragePeriods.length > 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {t('autoFleetGen.summary.limitedCoverageTitle')}
              </Typography>
              {mixedFleetSummary.warnings.limitedCoveragePeriods.map((period: { start: string; end: string; coverage: number }, idx: number) => (
                <Typography key={idx} variant="caption" display="block">
                  {t('autoFleetGen.summary.limitedCoverageItem', { start: period.start, end: period.end, value: period.coverage.toFixed(0) })}
                </Typography>
              ))}
            </Alert>
          )}
          {mixedFleetSummary.warnings.complexScheduleVehicles && mixedFleetSummary.warnings.complexScheduleVehicles.length > 0 && (
            <Alert severity="info">
              <Typography variant="body2" fontWeight={600}>
                {t('autoFleetGen.summary.complexScheduleTitle')}
              </Typography>
              <Typography variant="caption">
                {t(
                  mixedFleetSummary.warnings.complexScheduleVehicles.length === 1
                    ? 'autoFleetGen.summary.complexScheduleBodyOne'
                    : 'autoFleetGen.summary.complexScheduleBodyOther',
                  { total: mixedFleetSummary.warnings.complexScheduleVehicles.length }
                )}
              </Typography>
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FleetSummaryCards;

